// src/components/dashboard/WelcomePrompt.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Target, TrendingUp, Flame, Trophy, Star, Award, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

interface WelcomePromptProps {
  userName: string;
  currentStreak: number;
  onDismiss: () => void;
  onTestNow: () => Promise<void>;
  onSuccess?: () => void;
}

const celebrationEmojis = ['🎉', '🎊', '✨', '⭐', '🌟', '💫', '🎯', '🔥', '⚡', '🏆', '👏', '🎈'];

export const WelcomePrompt: React.FC<WelcomePromptProps> = ({
  userName,
  currentStreak,
  onDismiss,
  onTestNow,
  onSuccess,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  const handleTestNow = async () => {
    setIsSubmitting(true);
    try {
      console.log('🎯 Modal: Recording test...');
      await onTestNow();
      console.log('✅ Modal: Test recorded!');
      
      // Show celebration animation only if motion is allowed
      if (!prefersReducedMotion) {
        setShowCelebration(true);
        
        // Dismiss modal after celebration
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(onDismiss, 300);
        }, 4000); // 4 seconds of celebration
      } else {
        // For reduced motion, dismiss quickly with simple feedback
        setTimeout(() => {
          setIsVisible(false);
          setTimeout(onDismiss, 300);
        }, 1000); // 1 second simple confirmation
      }
      
      // Call success callback immediately
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('❌ Error submitting test:', error);
      setIsSubmitting(false);
      alert('Failed to record testing. Please try again.');
    }
  };

  const getMotivationalMessage = () => {
    if (currentStreak === 0) {
      return {
        emoji: '🚀',
        title: 'Start Your Journey!',
        message: 'Record your first testing session and begin building your streak!',
        color: 'from-blue-500 to-cyan-500',
      };
    } else if (currentStreak < 7) {
      return {
        emoji: '🔥',
        title: `${currentStreak} Day Streak!`,
        message: "You're doing great! Keep it going to reach Week Warrior status!",
        color: 'from-orange-500 to-red-500',
      };
    } else if (currentStreak < 30) {
      return {
        emoji: '🌟',
        title: `${currentStreak} Day Streak!`,
        message: "Amazing consistency! You're on your way to Month Master!",
        color: 'from-yellow-500 to-amber-500',
      };
    } else {
      return {
        emoji: '👑',
        title: `${currentStreak} Day Streak!`,
        message: "You're unstoppable! Keep dominating the leaderboard!",
        color: 'from-purple-500 to-pink-500',
      };
    }
  };

  const motivation = getMotivationalMessage();

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDismiss}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            {/* Floating particles in background - only if motion is allowed */}
            {!prefersReducedMotion && [...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute text-4xl"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                  opacity: 0.2,
                }}
                animate={{
                  y: [null, -100],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              >
                {['✨', '⭐', '💫', '🌟'][Math.floor(Math.random() * 4)]}
              </motion.div>
            ))}

            {/* Celebration Effects */}
            <AnimatePresence>
              {showCelebration && (
                <>
                  {/* Massive Confetti Explosion */}
                  <div className="absolute inset-0 pointer-events-none overflow-visible z-[60]">
                    {[...Array(80)].map((_, i) => {
                      const emoji = celebrationEmojis[Math.floor(Math.random() * celebrationEmojis.length)];
                      const angle = (i / 80) * Math.PI * 2;
                      const distance = 200 + Math.random() * 300;
                      const x = Math.cos(angle) * distance;
                      const y = Math.sin(angle) * distance;

                      return (
                        <motion.div
                          key={`confetti-${i}`}
                          className="absolute text-4xl"
                          style={{
                            left: '50%',
                            top: '50%',
                          }}
                          initial={{
                            x: 0,
                            y: 0,
                            scale: 0,
                            rotate: 0,
                            opacity: 1,
                          }}
                          animate={{
                            x: x,
                            y: y,
                            scale: [0, 1.8, 1.2, 0],
                            rotate: Math.random() * 1080 - 540,
                            opacity: [0, 1, 1, 0],
                          }}
                          exit={{ opacity: 0 }}
                          transition={{
                            duration: 2.5,
                            delay: i * 0.015,
                            ease: 'easeOut',
                          }}
                        >
                          {emoji}
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Giant Success Message */}
                  <motion.div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] pointer-events-none"
                    initial={{ scale: 0, opacity: 0, rotate: -180 }}
                    animate={{ scale: [0, 1.3, 1], opacity: [0, 1, 1], rotate: 0 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  >
                    <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 text-white px-12 py-8 rounded-3xl shadow-2xl border-4 border-white">
                      <motion.div
                        className="text-center"
                        animate={{
                          scale: [1, 1.15, 1],
                        }}
                        transition={{
                          duration: 0.6,
                          repeat: 4,
                          ease: 'easeInOut',
                        }}
                      >
                        <div className="text-7xl mb-3">🎉</div>
                        <div className="text-4xl font-bold mb-2">Awesome!</div>
                        <div className="text-xl font-medium">All Done for Today!</div>
                        <div className="text-base opacity-90 mt-2">Keep up the amazing work! 🚀</div>
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Firework Bursts */}
                  {[Trophy, Star, Award, Zap].map((Icon, i) => (
                    <motion.div
                      key={`firework-${i}`}
                      className="absolute left-1/2 top-1/2 pointer-events-none z-[65]"
                      initial={{ scale: 0, opacity: 1 }}
                      animate={{
                        scale: [0, 2.5, 0],
                        opacity: [1, 1, 0],
                        x: [0, Math.cos((i / 4) * Math.PI * 2) * 250],
                        y: [0, Math.sin((i / 4) * Math.PI * 2) * 250],
                        rotate: [0, 360],
                      }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 2,
                        delay: 0.3 + i * 0.15,
                        ease: 'easeOut',
                      }}
                    >
                      <Icon className="w-16 h-16 text-yellow-400 drop-shadow-lg" />
                    </motion.div>
                  ))}

                  {/* Sparkle Ring */}
                  {[...Array(16)].map((_, i) => (
                    <motion.div
                      key={`sparkle-${i}`}
                      className="absolute left-1/2 top-1/2 pointer-events-none z-[65]"
                      initial={{ scale: 0, opacity: 1 }}
                      animate={{
                        scale: [0, 2, 0],
                        opacity: [1, 1, 0],
                        x: Math.cos((i / 16) * Math.PI * 2) * 150,
                        y: Math.sin((i / 16) * Math.PI * 2) * 150,
                      }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 1.5,
                        delay: i * 0.04,
                        ease: 'easeOut',
                      }}
                    >
                      <Sparkles className="w-10 h-10 text-yellow-400" />
                    </motion.div>
                  ))}

                  {/* Ripple Waves */}
                  {[0, 1, 2, 3].map((i) => (
                    <motion.div
                      key={`ripple-${i}`}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-8 border-yellow-400 pointer-events-none z-[55]"
                      initial={{ scale: 0, opacity: 1, width: 100, height: 100 }}
                      animate={{ 
                        scale: [0, 4], 
                        opacity: [1, 0],
                      }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 2,
                        delay: i * 0.3,
                        ease: 'easeOut',
                      }}
                    />
                  ))}
                </>
              )}
            </AnimatePresence>

            {/* Modal Card */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ 
                scale: showCelebration ? 0.8 : 1, 
                opacity: showCelebration ? 0.3 : 1, 
                y: 0,
                filter: showCelebration ? 'blur(4px)' : 'blur(0px)',
              }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              transition={{ duration: 0.3 }}
              className="relative bg-background border-2 border-primary rounded-3xl p-8 max-w-lg w-full shadow-2xl overflow-hidden"
            >
              {/* Animated Background Gradient */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${motivation.color} opacity-10`}
                animate={!prefersReducedMotion ? {
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                } : {}}
                transition={{
                  duration: 4,
                  repeat: prefersReducedMotion ? 0 : Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Close Button */}
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-20"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Content */}
              <div className="relative z-10 text-center space-y-6">
                {/* Animated Emoji */}
                <motion.div
                  className="text-8xl"
                  animate={!prefersReducedMotion ? {
                    scale: [1, 1.2, 1],
                    rotate: [-5, 5, -5],
                  } : {}}
                  transition={{
                    duration: 2,
                    repeat: prefersReducedMotion ? 0 : Infinity,
                    ease: 'easeInOut',
                  }}
                >
                  {motivation.emoji}
                </motion.div>

                {/* Welcome Message */}
                <div>
                  <motion.h2
                    className="text-3xl font-bold mb-2"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    Welcome back, {userName}! 👋
                  </motion.h2>
                  <motion.h3
                    className={`text-2xl font-bold bg-gradient-to-r ${motivation.color} bg-clip-text text-transparent mb-3`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {motivation.title}
                  </motion.h3>
                  <motion.p
                    className="text-muted-foreground text-lg"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    {motivation.message}
                  </motion.p>
                </div>

                {/* Stats Preview */}
                <motion.div
                  className="grid grid-cols-3 gap-3"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="p-3 rounded-xl bg-muted/50 border border-primary/30">
                    <Flame className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold">{currentStreak}</div>
                    <div className="text-xs text-muted-foreground">Streak</div>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/50 border border-primary/30">
                    <Target className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold">Today</div>
                    <div className="text-xs text-muted-foreground">Test Now</div>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/50 border border-primary/30">
                    <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-1" />
                    <div className="text-2xl font-bold">{currentStreak + 1}</div>
                    <div className="text-xs text-muted-foreground">Next</div>
                  </div>
                </motion.div>

                {/* Call to Action */}
                <motion.div
                  className="space-y-3 pt-2"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <motion.div
                    whileHover={!isSubmitting ? { scale: 1.05, y: -2 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                  >
                    <Button
                      onClick={handleTestNow}
                      disabled={isSubmitting}
                      className="w-full h-16 text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-xl relative overflow-hidden group disabled:opacity-70"
                    >
                      {!isSubmitting && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover:opacity-30"
                          animate={{
                            x: ['-100%', '100%'],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {isSubmitting ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            >
                              ⏳
                            </motion.div>
                            Recording...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-6 h-6" />
                            Yes! I Tested Today! 🎯
                          </>
                        )}
                      </span>
                    </Button>
                  </motion.div>

                  <Button
                    onClick={handleDismiss}
                    disabled={isSubmitting}
                    variant="ghost"
                    className="w-full text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    I'll do it later
                  </Button>
                </motion.div>

                {/* Encouraging Footer */}
                <motion.div
                  className="pt-4 border-t border-border"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <p className="text-sm text-muted-foreground">
                    💡 <span className="font-semibold">Pro tip:</span> Testing early in the day helps maintain your streak!
                  </p>
                </motion.div>
              </div>

              {/* Decorative Corner Sparkles */}
              {!prefersReducedMotion && [
                { top: '10px', left: '10px', delay: 0 },
                { top: '10px', right: '10px', delay: 0.5 },
                { bottom: '10px', left: '10px', delay: 1 },
                { bottom: '10px', right: '10px', delay: 1.5 },
              ].map((pos, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  style={pos}
                  animate={{
                    scale: [1, 1.5, 1],
                    rotate: [0, 180, 360],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: pos.delay,
                    ease: 'easeInOut',
                  }}
                >
                  <Sparkles className="w-6 h-6 text-primary" />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

